<template>
  <div :class="$style.root">
    <button :class="$style.root__close">
      <IconClose @click="$emit('close')" />
    </button>
    <header :class="$style.root__header">
      <p :class="$style.header__text">検索条件を更新</p>
    </header>
    <div :class="$style.root__content">
      <p :class="$style.content__comment">
        「{{
          selectedFilteringRuleLabel
        }}」を現在の選択条件と新しい検索条件名に更新しますか？
      </p>
      <div :class="$style.content__edit">
        <p>新しい検索条件名</p>
        <div :class="$style.edit__input">
          <AppPopover
            v-show="isFocused && hasNewTextInput"
            :class="$style.input__popover"
          >
            <ul :class="$style.popover">
              <li
                v-for="message in validationMessages"
                :key="message"
                :class="[
                  $style.popover__message,
                  $style['popover__message--invalid'],
                ]"
              >
                <IconExclamationCircle :class="$style.popover__icon" />
                <p>{{ message }}</p>
              </li>
              <li
                :class="[
                  $style.popover__message,
                  $style['popover__message--valid'],
                ]"
                v-show="!hasValidationMessages"
              >
                <IconCheckCircle :class="$style.popover__icon" />
                <p>設定可能な検索条件名です</p>
              </li>
            </ul>
          </AppPopover>
          <DolphinTextInput
            :class="$style.input__box"
            :placeholder="selectedFilteringRuleLabel"
            v-model="inputText"
            @focus="isFocused = true"
            @blur="isFocused = false"
            name="text"
          />
          <p :class="$style.input__annotation">
            ※ 入力せず更新した場合、検索条件名は変更されません
          </p>
        </div>
      </div>
      <FlatButton
        :class="$style.content__button"
        variant="primary"
        size="medium"
        @click="submit"
      >
        検索条件を更新
      </FlatButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import IconClose from '@/components/icons/IconClose.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import AppPopover from '@/components/atoms/AppPopover.vue'
import IconCheckCircle from '@/components/icons/IconCheckCircle.vue'
import IconExclamationCircle from '@/components/icons/IconExclamationCircle.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'

const text = ref('')
const isFocused = ref(false)

const props = withDefaults(
  defineProps<{
    selectedFilteringRuleLabel: string
  }>(),
  {
    selectedFilteringRuleLabel: '',
  }
)

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'submit', submittedText: string): void
}>()

const inputText = computed({
  get(): string {
    return text.value
  },
  set(inputText: string): void {
    text.value = inputText
  },
})

const submittedText = computed(() => {
  return text.value === '' ? props.selectedFilteringRuleLabel : text.value
})

const submit = () => {
  emit('submit', submittedText.value)
  emit('close')
}

const isNotJustWhitespace = computed(() => {
  return inputText.value.trim().length > 0
})

const hasNewTextInput = computed(() => {
  return inputText.value.length > 0
})

const hasValidationMessages = computed(() => {
  return validationMessages.value.length > 0
})

const validationMessages = computed(() =>
  [
    {
      invalid: !isNotJustWhitespace.value && hasNewTextInput.value!,
      message: 'スペースなど空白文字のみを使用することはできません',
    },
  ]
    .filter(({ invalid }) => invalid)
    .map(({ message }) => message)
)
</script>

<style lang="scss" module>
.root {
  width: 600px;
  height: 100%;
  padding: 22px;

  &__close {
    display: block;
    width: 12px;
    margin-left: auto;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
  }
}

.header {
  &__text {
    font-size: 18px;
    font-weight: bold;
    text-align: center;
  }
}

.content {
  &__comment {
    font-size: 16px;
  }

  &__button {
    margin-top: 24px;
  }

  &__edit {
    display: flex;
    width: 100%;
    align-items: baseline;
    justify-content: space-evenly;
    margin-top: 24px;
  }
}

.edit {
  &__input {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}

.input {
  &__popover {
    position: absolute;
    transform: translateY(-90%);
  }

  &__box {
    width: 320px;
    height: 48px;
    border: solid 1px $color-border-low-emphasis;
    border-radius: $border-radius-rounder;
    font-size: 14px;
  }

  &__annotation {
    margin-top: 4px;
    color: $color-text-and-object-medium-emphasis;
    font-size: 12px;
  }
}

.popover {
  &__message {
    display: flex;
    align-items: center;

    &--invalid {
      color: $color-caution-accent;
    }

    &--valid {
      color: $color-success-object;
    }
  }

  &__icon {
    margin-right: 0.5em;
  }
}
</style>
